import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Save, FileDown, Upload, File, Download, X, Eye } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const getBaseURL = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
};

export function CustomerInfo() {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [loading, setLoading] = useState(false);
  const [spreadsheetData, setSpreadsheetData] = useState({
    headers: ["Column 1", "Column 2", "Column 3"],
    rows: [
      ["", "", ""],
      ["", "", ""],
      ["", "", ""]
    ]
  });
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const loadCustomers = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${getBaseURL()}/api/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sort alphabetically by name
      const sorted = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setCustomers(sorted);
    } catch (error) {
      console.error("Load customers error:", error);
      toast.error("Failed to load customers");
    }
  }, []);

  const loadCustomerInfo = useCallback(async (customerId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(
        `${getBaseURL()}/api/customer-info/${customerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.data && Object.keys(response.data.data).length > 0) {
        setSpreadsheetData(response.data.data);
      } else {
        // Reset to default empty spreadsheet
        setSpreadsheetData({
          headers: ["Column 1", "Column 2", "Column 3"],
          rows: [
            ["", "", ""],
            ["", "", ""],
            ["", "", ""]
          ]
        });
      }
    } catch (error) {
      console.error("Load customer info error:", error);
      toast.error("Failed to load customer info");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCustomerFiles = useCallback(async (customerId) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(
        `${getBaseURL()}/api/customer-info/${customerId}/files`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFiles(response.data || []);
    } catch (error) {
      console.error("Load files error:", error);
      setFiles([]);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    if (selectedCustomer) {
      loadCustomerInfo(selectedCustomer);
      loadCustomerFiles(selectedCustomer);
    }
  }, [selectedCustomer, loadCustomerInfo, loadCustomerFiles]);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedCustomer) {
      toast.error("Please select a customer first");
      return;
    }

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size exceeds 10MB limit");
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem("auth_token");
      const formData = new FormData();
      formData.append("file", file);

      await axios.post(
        `${getBaseURL()}/api/customer-info/${selectedCustomer}/files`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      toast.success("File uploaded successfully");
      loadCustomerFiles(selectedCustomer);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileDownload = async (file) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(
        `${getBaseURL()}/api/customer-info/${selectedCustomer}/files/${file.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file");
    }
  };

  const handleFileView = async (file) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(
        `${getBaseURL()}/api/customer-info/${selectedCustomer}/files/${file.id}/download?inline=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );

      const blob = new Blob([response.data], { type: file.content_type || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const newTab = window.open(url, "_blank");
      if (!newTab) {
        toast.error("Popup blocked. Please allow popups to view files.");
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("View error:", error);
      toast.error("Failed to view file");
    }
  };

  const handleFileDelete = async (file) => {
    if (!window.confirm(`Are you sure you want to delete "${file.original_filename}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("auth_token");
      await axios.delete(
        `${getBaseURL()}/api/customer-info/${selectedCustomer}/files/${file.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("File deleted successfully");
      loadCustomerFiles(selectedCustomer);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete file");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleSave = async () => {
    if (!selectedCustomer) {
      toast.error("Please select a customer first");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("auth_token");
      
      await axios.post(
        `${getBaseURL()}/api/customer-info/${selectedCustomer}`,
        { data: spreadsheetData },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Customer info saved successfully");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save customer info");
    } finally {
      setSaving(false);
    }
  };

  const handleCellChange = (rowIndex, colIndex, value) => {
    const newData = { ...spreadsheetData };
    newData.rows[rowIndex][colIndex] = value;
    setSpreadsheetData(newData);
  };

  const handleHeaderChange = (colIndex, value) => {
    const newData = { ...spreadsheetData };
    newData.headers[colIndex] = value;
    setSpreadsheetData(newData);
  };

  const addRow = () => {
    const newData = { ...spreadsheetData };
    newData.rows.push(new Array(spreadsheetData.headers.length).fill(""));
    setSpreadsheetData(newData);
  };

  const addColumn = () => {
    const newData = { ...spreadsheetData };
    newData.headers.push(`Column ${newData.headers.length + 1}`);
    newData.rows = newData.rows.map(row => [...row, ""]);
    setSpreadsheetData(newData);
  };

  const deleteRow = (rowIndex) => {
    if (spreadsheetData.rows.length <= 1) {
      toast.error("Must have at least one row");
      return;
    }
    const newData = { ...spreadsheetData };
    newData.rows.splice(rowIndex, 1);
    setSpreadsheetData(newData);
  };

  const deleteColumn = (colIndex) => {
    if (spreadsheetData.headers.length <= 1) {
      toast.error("Must have at least one column");
      return;
    }
    const newData = { ...spreadsheetData };
    newData.headers.splice(colIndex, 1);
    newData.rows = newData.rows.map(row => {
      const newRow = [...row];
      newRow.splice(colIndex, 1);
      return newRow;
    });
    setSpreadsheetData(newData);
  };

  const handleExportPDF = async () => {
    if (!selectedCustomer) {
      toast.error("Please select a customer first");
      return;
    }

    try {
      setExporting(true);
      const token = localStorage.getItem("auth_token");
      
      // Get customer name for filename
      const customer = customers.find(c => c.id === selectedCustomer);
      const customerName = customer?.name || "customer";
      
      const response = await axios.get(
        `${getBaseURL()}/api/customer-info/${selectedCustomer}/export-pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customer_report_${customerName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF report downloaded successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export PDF report");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Customer Info</h1>
          <p className="text-gray-600 mt-2">Manage custom customer information</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleExportPDF} 
            disabled={!selectedCustomer || exporting}
            data-testid="export-pdf-button"
          >
            <FileDown className="h-4 w-4 mr-2" />
            {exporting ? "Exporting..." : "Export PDF"}
          </Button>
          <Button onClick={handleSave} disabled={!selectedCustomer || saving} data-testid="save-button">
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {/* Customer Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Select Customer</CardTitle>
          <CardDescription>Choose a customer to view or edit their information</CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
            <SelectTrigger className="w-full max-w-md">
              <SelectValue placeholder="Select a customer" />
            </SelectTrigger>
            <SelectContent>
              {customers.map((customer) => (
                <SelectItem key={customer.id} value={customer.id}>
                  {customer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Spreadsheet */}
      {selectedCustomer && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Customer Information Sheet</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={addRow}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Row
                </Button>
                <Button variant="outline" size="sm" onClick={addColumn}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Column
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 p-2 w-12">#</th>
                      {/* Column headers use index as key since columns are position-based without stable IDs */}
                      {spreadsheetData.headers.map((header, colIndex) => (
                        <th key={`col-${colIndex}`} className="border border-gray-300 p-0">
                          <div className="flex items-center">
                            <Input
                              value={header}
                              onChange={(e) => handleHeaderChange(colIndex, e.target.value)}
                              className="border-0 rounded-none font-semibold text-center"
                              placeholder={`Column ${colIndex + 1}`}
                            />
                            <button
                              onClick={() => deleteColumn(colIndex)}
                              className="p-2 hover:bg-red-100 text-red-600"
                              title="Delete column"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Rows use index as key since spreadsheet rows are position-based */}
                    {spreadsheetData.rows.map((row, rowIndex) => (
                      <tr key={`row-${rowIndex}`}>
                        <td className="border border-gray-300 p-2 bg-gray-50 text-center text-sm font-medium">
                          <div className="flex items-center justify-center gap-1">
                            <span>{rowIndex + 1}</span>
                            <button
                              onClick={() => deleteRow(rowIndex)}
                              className="p-1 hover:bg-red-100 text-red-600 rounded"
                              title="Delete row"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </td>
                        {row.map((cell, colIndex) => (
                          <td key={`cell-${rowIndex}-${colIndex}`} className="border border-gray-300 p-0">
                            <Input
                              value={cell}
                              onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                              className="border-0 rounded-none"
                              placeholder="Enter data"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* File Uploads Section */}
      {selectedCustomer && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Customer Files</CardTitle>
                <CardDescription>Upload and manage files for this customer</CardDescription>
              </div>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="file-upload-input"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-file-button"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? "Uploading..." : "Upload File"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {files.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <File className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No files uploaded yet</p>
                <p className="text-sm">Click "Upload File" to add documents</p>
              </div>
            ) : (
              <div className="space-y-2">
                {files.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                    data-testid={`file-item-${file.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <File className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-sm">{file.original_filename}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.file_size)} • Uploaded by {file.uploaded_by}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFileView(file)}
                        title="View"
                        data-testid={`view-file-${file.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFileDownload(file)}
                        title="Download"
                        data-testid={`download-file-${file.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFileDelete(file)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50"
                        title="Delete"
                        data-testid={`delete-file-${file.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!selectedCustomer && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <p>Please select a customer to view or edit their information</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
